// Phase 5Z.12 — Profilanfragen (Profile Change Requests) admin page
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";
import { StatusBadge } from "@/components/Shared";
import DataTable from "@/components/DataTable";
import { CheckCircle2, XCircle, Clock, ListChecks, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ModuleHint from "@/components/ModuleHint";
import FirmFilter from "@/components/FirmFilter";
import { fmtDateTime } from "@/lib/format";

const FIELD_LABELS = {
  phone: "Telefon",
  private_email: "Private E-Mail",
  email: "Firmen-E-Mail",
  address: "Adresse",
  address_extra: "Adresse Zusatz",
  zip_code: "PLZ",
  city: "Ort",
  emergency_contact_name: "Notfall — Name",
  emergency_contact_phone: "Notfall — Telefon",
  emergency_contact_relationship: "Notfall — Beziehung",
};

export default function Profilanfragen() {
  const { can } = usePermissions();
  const canApprove = can("profilanfragen", "approve");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get("/profile-change-requests", { params: statusFilter && statusFilter !== "all" ? { status: statusFilter } : {} }),
        api.get("/profile-change-requests/_summary"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [statusFilter]);

  async function approve(req) {
    if (!window.confirm(`Profilanfrage von ${req.employee_name} freigeben?\n\nDie Änderungen werden direkt im Mitarbeiter-Profil gespeichert.`)) return;
    try {
      await api.post(`/profile-change-requests/${req.id}/approve`);
      toast.success("Anfrage freigegeben — Profil aktualisiert");
      setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }
  async function reject(req) {
    const reason = window.prompt(`Anfrage von ${req.employee_name} ablehnen?\n\nGrund (sichtbar für Fahrer):`);
    if (reason === null) return;
    try {
      await api.post(`/profile-change-requests/${req.id}/reject`, { reason });
      toast.success("Anfrage abgelehnt");
      setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  }

  return (
    <div className="p-6 md:p-8" data-testid="profilanfragen-page">
      <ModuleHint moduleKey="profilanfragen" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-zinc-100">Profilanfragen</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Änderungswünsche der Fahrer am eigenen Stammdatensatz</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard label="Gesamt" value={summary.total} icon={ListChecks} active={statusFilter === "all"}
                     onClick={() => setStatusFilter("all")} testid="prq-card-total" />
        <SummaryCard label="Ausstehend" value={summary.pending} icon={Clock} active={statusFilter === "pending"}
                     color="text-amber-400 border-amber-500/30 bg-amber-500/5"
                     onClick={() => setStatusFilter("pending")} testid="prq-card-pending" />
        <SummaryCard label="Genehmigt" value={summary.approved} icon={CheckCircle2} active={statusFilter === "approved"}
                     color="text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
                     onClick={() => setStatusFilter("approved")} testid="prq-card-approved" />
        <SummaryCard label="Abgelehnt" value={summary.rejected} icon={XCircle} active={statusFilter === "rejected"}
                     color="text-red-400 border-red-500/30 bg-red-500/5"
                     onClick={() => setStatusFilter("rejected")} testid="prq-card-rejected" />
      </div>

      <DataTable
        rows={items}
        loading={loading}
        filters={<FirmFilter compact />}
        searchableFields={["employee_name", "reason", (r) => Object.keys(r.changes || {}).join(" ")]}
        searchPlaceholder="Mitarbeiter, Begründung, Feldnamen..."
        testid="profilanfragen-table"
        emptyTitle="Keine Anfragen"
        emptyHint={statusFilter === "pending" ? "Aktuell sind keine Anfragen offen." : "Versuchen Sie einen anderen Filter."}
        columns={[
          { key: "submitted_at", label: "Eingereicht", sortable: true,
            render: (r) => <span className="font-mono text-xs text-zinc-300">{fmtDateTime(r.submitted_at)}</span> },
          { key: "employee_name", label: "Fahrer",
            render: (r) => <span className="text-zinc-100 font-medium">{r.employee_name}</span> },
          { key: "_fields", label: "Felder",
            render: (r) => (
              <div className="flex flex-wrap gap-1 max-w-[28rem]">
                {Object.keys(r.changes || {}).map((k) => (
                  <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">
                    {FIELD_LABELS[k] || k}
                  </span>
                ))}
              </div>
            ) },
          { key: "reason", label: "Begründung",
            render: (r) => <span className="text-xs text-zinc-400 italic line-clamp-1 max-w-[20rem]">{r.reason || "—"}</span> },
          { key: "status", label: "Status", sortable: true,
            render: (r) => <StatusBadge status={r.status === "pending" ? "offen" : r.status === "approved" ? "freigegeben" : "abgelehnt"} /> },
          { key: "_actions", label: "", headerClassName: "text-right", className: "text-right",
            render: (r) => (
              <div className="inline-flex items-center gap-1">
                <button onClick={(e) => { e.stopPropagation(); setDetail(r); }} data-testid={`prq-detail-${r.id}`}
                        className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                  Ansehen
                </button>
                {canApprove && r.status === "pending" && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); approve(r); }} title="Freigeben"
                            data-testid={`prq-approve-${r.id}`}
                            className="p-1.5 text-zinc-400 hover:text-emerald-400">
                      <CheckCircle2 className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); reject(r); }} title="Ablehnen"
                            data-testid={`prq-reject-${r.id}`}
                            className="p-1.5 text-zinc-400 hover:text-red-400">
                      <XCircle className="w-3.5 h-3.5"/>
                    </button>
                  </>
                )}
              </div>
            ) },
        ]}
      />

      {detail && (
        <DetailModal request={detail} canApprove={canApprove}
                     onApprove={() => approve(detail)}
                     onReject={() => reject(detail)}
                     onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, active, color, onClick, testid }) {
  const base = "bg-zinc-900 border border-zinc-800 text-zinc-100";
  return (
    <button onClick={onClick} data-testid={testid}
            className={`text-left p-4 rounded-md transition-colors ${active ? (color || "border-blue-500/40 bg-blue-500/5") : base} hover:border-zinc-700`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-400 uppercase tracking-wider font-semibold">{label}</span>
        <Icon className={`w-4 h-4 ${active ? "" : "text-zinc-500"}`} />
      </div>
      <div className="mt-1 text-2xl font-display font-semibold">{value}</div>
    </button>
  );
}

function DetailModal({ request, canApprove, onApprove, onReject, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="prq-detail-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10">
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-100">Profilanfrage · {request.employee_name}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Eingereicht {fmtDateTime(request.submitted_at)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-zinc-400 hover:text-zinc-100">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded p-3">
            <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Geänderte Felder</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800">
                  <th className="py-1.5 font-medium">Feld</th>
                  <th className="py-1.5 font-medium">Vorher</th>
                  <th className="py-1.5 font-medium">Neu</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(request.changes || {}).map(([k, v]) => (
                  <tr key={k} className="border-b border-zinc-800/50 last:border-b-0">
                    <td className="py-2 text-zinc-300 text-xs">{FIELD_LABELS[k] || k}</td>
                    <td className="py-2 text-zinc-500 text-xs line-through">{request.before?.[k] || "—"}</td>
                    <td className="py-2 text-zinc-100 text-xs font-medium">{v || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {request.reason && (
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Begründung Fahrer</div>
              <div className="text-sm text-zinc-200 bg-zinc-950 border-l-2 border-amber-500 px-3 py-2 rounded">
                {request.reason}
              </div>
            </div>
          )}

          {request.status !== "pending" && (
            <div className="text-xs text-zinc-400">
              <span className="font-medium">{request.status === "approved" ? "Freigegeben" : "Abgelehnt"}</span>
              {" "}am {fmtDateTime(request.decided_at)} von {request.decided_by_user_name || "—"}
              {request.decision_reason && (
                <div className="mt-1 text-red-400 italic">Grund: {request.decision_reason}</div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
          {canApprove && request.status === "pending" && (
            <>
              <button onClick={onReject} data-testid="prq-modal-reject"
                      className="text-sm px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5">
                <XCircle className="w-4 h-4" /> Ablehnen
              </button>
              <button onClick={onApprove} data-testid="prq-modal-approve"
                      className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Freigeben
              </button>
            </>
          )}
          <button onClick={onClose}
                  className="text-sm px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100">
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
